import {AddProjectPayload} from '../../api/types/project.type';
import {ProjectFormState} from '../../redux/slice/projectFormSlice';

export const validateProjectForm = (
  form: ProjectFormState,
  roleId: number
): boolean => {
  const isProjectNameValid =
    !!form.projectName?.trim() &&
    /^(?! *$)[A-Za-z0-9 _-]{1,50}$/.test(form.projectName);

  const isAlphaCodeValid =
    roleId === -2 ||
    (!!form.alphaCode?.trim() &&
      /^(?! *$)[A-Za-z0-9 _-]{1,16}$/.test(form.alphaCode));

  const isClientIdValid = !!form.selectedClientId && form.selectedClientId > 0;

  const hasProjectServer = form.projectServerSelected?.length > 0;
  const hasProductionServer = form.productionServerSelected?.length > 0;
  const hasLoadServer = form.loadServerSelected?.length > 0;
  const projectFolderPath =
    form.projectFolderPath != '' &&
    /^[a-zA-Z0-9-_\\ ]+$/.test(form.projectFolderPath);
  const ingestionFolderPath =
    form.ingestionFolderPath != '' &&
    /^[a-zA-Z0-9-_\\ ]+$/.test(form.ingestionFolderPath);
  const productionFolderPath =
    form.productionFolderPath != '' &&
    /^[a-zA-Z0-9-_\\ ]+$/.test(form.productionFolderPath);

  const prefix =
    form?.prefix === '' || /^[A-Za-z0-9@\-_$%^+=~]*$/.test(form?.prefix);
  const suffix =
    form?.suffix === '' || /^[A-Za-z0-9@\-_$%^+=~]*$/.test(form?.suffix);
  return (
    isProjectNameValid &&
    isAlphaCodeValid &&
    isClientIdValid &&
    hasProjectServer &&
    hasProductionServer &&
    hasLoadServer &&
    projectFolderPath &&
    ingestionFolderPath &&
    productionFolderPath &&
    prefix &&
    suffix
  );
};

export const mapFormToAddProjectPayload = (
  form: ProjectFormState,
  selectedClientId: number | null,
  type: string | undefined
): AddProjectPayload => {
  const {
    projectName,
    alphaCode,
    isActiveProject,
    projectFolderPath,
    ingestionFolderPath,
    productionFolderPath,
    projectServerSelected,
    loadServerSelected,
    productionServerSelected,
    toggles,
    isAnalyticsActive,
    isRedactionPropagationActive,
    analyticsAutomationSelected,
    analyticsComponentsSelected,
    prefix,
    suffix,
    minLength,
    analyticsEngine,
    encryptedProjectId,
    deleteProject,
    selectedProject,
    projectDBServerId,
    isGenerativeAIActive,
    generativeAISelected,
  } = form;

  return {
    ...(type === 'edit' && {EncryptedProjectID: encryptedProjectId}),
    ...(type === 'edit' && {isEditProject: true}),
    ...(type === 'edit' && {isDeleted: deleteProject}),
    projectLabel: projectName?.trim(),
    hcode: alphaCode || '',
    clientEntityID: selectedClientId ?? 0,
    domainType: 2,
    isActive: isActiveProject ?? true,
    projectDetailsModel: {
      ...(type !== 'edit' &&
        selectedProject != null && {
          templateProjectID: Number(selectedProject?.key),
        }),
      projectDBServerID: projectDBServerId,
      timeZone: 'UTC',
      projectFolder: projectFolderPath?.trim() || '',
      ingestionFolder: ingestionFolderPath?.trim() || '',
      productionFolder: productionFolderPath?.trim() || '',
      selectedProjectFileServers:
        projectServerSelected?.map((s) => s.id.toString()) || [],
      selectedLoadFileServers:
        loadServerSelected?.map((s) => s.id.toString()) || [],
      selectedProductionFileServers:
        productionServerSelected?.map((s) => s.id.toString()) || [],
      ...(type !== 'edit' && {
        shouldCopyUsers:
          toggles?.find((t) => t.key === 'users')?.checked || false,
        shouldCopySearches:
          toggles?.find((t) => t.key === 'savedSearches')?.checked || false,
        shouldCopyAssignments:
          toggles?.find((t) => t.key === 'assignments')?.checked || false,
        shouldCopyAssignedReviewers:
          toggles?.find((t) => t.key === 'assignedReviewers')?.checked || false,
        shouldCopyCustomReports:
          toggles?.find((t) => t.key === 'customReports')?.checked || false,
      }),
    },

    projectSettingsModel: {
      isRedactionPropagated: isRedactionPropagationActive || false,
      isEnabledAnalytics: isAnalyticsActive || false,
      autoAnalytics:
        analyticsAutomationSelected?.some((c) => c.id === 2) || false,
      autoIncrementalAnalytics:
        analyticsAutomationSelected?.some((c) => c.id === 3) || false,
      canTextualAnalytics:
        analyticsComponentsSelected?.some(
          (c) => c.label === 'Textual Analytics'
        ) || false,
      canEntityPIIExtraction:
        analyticsComponentsSelected?.some(
          (c) => c.label === 'Entity PII Extraction'
        ) || false,
      docIDPrefix: prefix || '',
      docIDSuffix: suffix || '',
      docIDMinLengthNo: minLength || '',
      analyticsEngineID: analyticsEngine?.value || 2,
      canUseGenAI: isGenerativeAIActive,
      aiInvestgate: generativeAISelected?.some((c) => c.id === 0) || false,
      aiReview: generativeAISelected?.some((c) => c.id === 1) || false,
      aiBatchSummarize: generativeAISelected?.some((c) => c.id === 2) || false,
    },
  };
};
